<template>
  <section class="time-calculator">
    <div class="page-header">
      <h1 class="page-title">时间计算器</h1>
      <p class="page-desc">在线时间加减、时间差计算工具</p>
    </div>

    <!-- 当前时间 -->
    <div class="current-time-bar">
      当前时间：<strong>{{ currentTimeStr }}</strong>
    </div>

    <!-- Tab 切换 -->
    <div class="tab-bar">
      <button
        :class="['tab-btn', { active: activeTab === 'addsub' }]"
        @click="activeTab = 'addsub'"
      >时间加减</button>
      <button
        :class="['tab-btn', { active: activeTab === 'diff' }]"
        @click="activeTab = 'diff'"
      >时间差计算</button>
    </div>

    <!-- ========== 时间加减 ========== -->
    <div v-if="activeTab === 'addsub'" class="calc-panel">
      <div class="calc-row">
        <div class="calc-group">
          <label class="calc-label">起始日期</label>
          <input type="date" v-model="addSubDate" class="calc-input" />
        </div>
        <div class="calc-group">
          <label class="calc-label">起始时间</label>
          <input type="time" v-model="addSubTime" step="1" class="calc-input" />
        </div>
        <button class="now-btn" @click="setAddSubNow">现在</button>
      </div>

      <div class="calc-row op-row">
        <div class="op-toggle">
          <button
            :class="['op-btn', { active: addSubOp === 'add' }]"
            @click="addSubOp = 'add'"
          >＋ 增加</button>
          <button
            :class="['op-btn', { active: addSubOp === 'subtract' }]"
            @click="addSubOp = 'subtract'"
          >－ 减少</button>
        </div>
      </div>

      <div class="calc-row input-row">
        <div class="calc-group">
          <label class="calc-label">天</label>
          <input type="number" v-model.number="addSubDays" min="0" class="calc-input short" placeholder="0" />
        </div>
        <div class="calc-group">
          <label class="calc-label">时</label>
          <input type="number" v-model.number="addSubHours" min="0" max="23" class="calc-input short" placeholder="0" />
        </div>
        <div class="calc-group">
          <label class="calc-label">分</label>
          <input type="number" v-model.number="addSubMinutes" min="0" max="59" class="calc-input short" placeholder="0" />
        </div>
        <div class="calc-group">
          <label class="calc-label">秒</label>
          <input type="number" v-model.number="addSubSeconds" min="0" max="59" class="calc-input short" placeholder="0" />
        </div>
      </div>

      <div class="calc-actions">
        <button class="calc-btn primary" @click="doAddSub">计算</button>
        <button class="calc-btn" @click="clearAddSub">清空</button>
      </div>

      <transition name="result-fade">
        <div v-if="addSubResult" class="result-box">
          <div class="result-label">计算结果</div>
          <div class="result-value">{{ addSubResult }}</div>
        </div>
      </transition>
    </div>

    <!-- ========== 时间差计算 ========== -->
    <div v-if="activeTab === 'diff'" class="calc-panel">
      <div class="diff-section">
        <h3 class="section-title">起始时间</h3>
        <div class="calc-row">
          <div class="calc-group">
            <label class="calc-label">日期</label>
            <input type="date" v-model="diffDate1" class="calc-input" />
          </div>
          <div class="calc-group">
            <label class="calc-label">时间</label>
            <input type="time" v-model="diffTime1" step="1" class="calc-input" />
          </div>
          <button class="now-btn" @click="setDiffStartNow">现在</button>
        </div>
      </div>

      <div class="diff-section">
        <h3 class="section-title">结束时间</h3>
        <div class="calc-row">
          <div class="calc-group">
            <label class="calc-label">日期</label>
            <input type="date" v-model="diffDate2" class="calc-input" />
          </div>
          <div class="calc-group">
            <label class="calc-label">时间</label>
            <input type="time" v-model="diffTime2" step="1" class="calc-input" />
          </div>
          <button class="now-btn" @click="setDiffEndNow">现在</button>
        </div>
      </div>

      <div class="calc-actions">
        <button class="calc-btn primary" @click="doDiff">计算</button>
        <button class="calc-btn" @click="clearDiff">清空</button>
        <button class="calc-btn" @click="swapDiff">⇄ 交换</button>
      </div>

      <transition name="result-fade">
        <div v-if="diffResult" class="result-box">
          <div class="result-label">时间差</div>
          <div class="result-value">{{ diffResult }}</div>
          <div class="result-detail" v-if="diffDetail">{{ diffDetail }}</div>
        </div>
      </transition>
    </div>

    <!-- 说明 -->
    <div class="help-block">
      <h3>使用说明</h3>
      <ul>
        <li><strong>时间加减</strong>：选择一个起始时间，输入要增加或减少的天/时/分/秒，点击计算得到结果时间。</li>
        <li><strong>时间差计算</strong>：输入两个时间点，计算它们之间的精确间隔（天、时、分、秒）。</li>
        <li>所有计算均为本地时间，不涉及时区转换。</li>
      </ul>
    </div>
  </section>
</template>

<script>
export default {
  name: 'TimeCalculatorPage',
  data() {
    const now = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
    const timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    return {
      activeTab: 'addsub',
      currentTimeStr: '',
      currentTimer: null,

      // 时间加减
      addSubDate: dateStr,
      addSubTime: timeStr,
      addSubOp: 'add',
      addSubDays: 0,
      addSubHours: 0,
      addSubMinutes: 0,
      addSubSeconds: 0,
      addSubResult: '',

      // 时间差
      diffDate1: dateStr,
      diffTime1: timeStr,
      diffDate2: dateStr,
      diffTime2: timeStr,
      diffResult: '',
      diffDetail: ''
    }
  },
  mounted() {
    this.updateCurrentTime()
    this.currentTimer = setInterval(this.updateCurrentTime, 1000)
  },
  beforeUnmount() {
    if (this.currentTimer) clearInterval(this.currentTimer)
  },
  methods: {
    updateCurrentTime() {
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      this.currentTimeStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    },

    fmtDateTime(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      const w = weekdays[date.getDay()]
      return `${y}年${m}月${d}日 ${w} ${h}:${min}:${s}`
    },

    setAddSubNow() {
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      this.addSubDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
      this.addSubTime = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    },

    doAddSub() {
      const dt = new Date(`${this.addSubDate}T${this.addSubTime}`)
      if (isNaN(dt.getTime())) {
        this.addSubResult = '请输入有效的时间'
        return
      }
      const totalSeconds = this.addSubDays * 86400 + this.addSubHours * 3600 + this.addSubMinutes * 60 + this.addSubSeconds
      const sign = this.addSubOp === 'add' ? 1 : -1
      const result = new Date(dt.getTime() + sign * totalSeconds * 1000)
      this.addSubResult = this.fmtDateTime(result)
    },

    clearAddSub() {
      this.addSubDays = 0
      this.addSubHours = 0
      this.addSubMinutes = 0
      this.addSubSeconds = 0
      this.addSubResult = ''
    },

    setDiffStartNow() {
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      this.diffDate1 = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
      this.diffTime1 = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    },

    setDiffEndNow() {
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      this.diffDate2 = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
      this.diffTime2 = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    },

    swapDiff() {
      const tmpDate = this.diffDate1
      const tmpTime = this.diffTime1
      this.diffDate1 = this.diffDate2
      this.diffTime1 = this.diffTime2
      this.diffDate2 = tmpDate
      this.diffTime2 = tmpTime
      this.doDiff()
    },

    doDiff() {
      const dt1 = new Date(`${this.diffDate1}T${this.diffTime1}`)
      const dt2 = new Date(`${this.diffDate2}T${this.diffTime2}`)
      if (isNaN(dt1.getTime()) || isNaN(dt2.getTime())) {
        this.diffResult = '请输入有效的时间'
        this.diffDetail = ''
        return
      }
      let diffMs = dt2.getTime() - dt1.getTime()
      const negative = diffMs < 0
      diffMs = Math.abs(diffMs)

      const totalSeconds = Math.floor(diffMs / 1000)
      const days = Math.floor(totalSeconds / 86400)
      const hours = Math.floor((totalSeconds % 86400) / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60

      const parts = []
      if (days > 0) parts.push(`${days} 天`)
      if (days > 0 || hours > 0) parts.push(`${hours} 时`)
      if (days > 0 || hours > 0 || minutes > 0) parts.push(`${minutes} 分`)
      parts.push(`${seconds} 秒`)

      const prefix = negative ? '（结束时间早于起始时间）' : ''
      this.diffResult = prefix + parts.join(' ')
      this.diffDetail = `合计 ${totalSeconds.toLocaleString()} 秒`
    },

    clearDiff() {
      this.diffResult = ''
      this.diffDetail = ''
    }
  }
}
</script>

<style scoped>
.time-calculator {
  max-width: 720px;
  margin: 0 auto 20px;
  padding: 0 16px;
}

.page-header {
  text-align: center;
  padding: 32px 0 16px;
}
.page-title {
  font-size: 26px;
  font-weight: 700;
  color: #1a1a2e;
}
.page-desc {
  font-size: 14px;
  color: #888;
  margin-top: 6px;
}

/* 当前时间条 */
.current-time-bar {
  text-align: center;
  font-size: 14px;
  color: #555;
  background: #fff;
  border-radius: 8px;
  padding: 10px 16px;
  margin-bottom: 20px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
}

/* Tab */
.tab-bar {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
}
.tab-btn {
  flex: 1;
  padding: 12px 0;
  font-size: 15px;
  font-weight: 600;
  border: none;
  background: transparent;
  color: #888;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}
.tab-btn.active {
  color: #fff;
  background: #1677ff;
}
.tab-btn:hover:not(.active) {
  background: #f5f7fa;
}

/* 计算面板 */
.calc-panel {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}
.calc-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.calc-row:last-child {
  margin-bottom: 0;
}
.calc-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.calc-label {
  font-size: 12px;
  color: #888;
  font-weight: 500;
}
.calc-input {
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 15px;
  color: #333;
  outline: none;
  transition: border-color 0.2s;
  min-width: 130px;
}
.calc-input:focus {
  border-color: #1677ff;
  box-shadow: 0 0 0 2px rgba(22,119,255,0.1);
}
.calc-input.short {
  min-width: 70px;
  width: 80px;
}

/* 现在按钮 */
.now-btn {
  padding: 8px 16px;
  font-size: 13px;
  background: #f0f5ff;
  color: #1677ff;
  border: 1px solid #b7d4ff;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  align-self: flex-end;
  margin-bottom: -2px;
}
.now-btn:hover {
  background: #d6e8ff;
}

/* 操作切换 */
.op-row {
  justify-content: center;
}
.op-toggle {
  display: flex;
  gap: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}
.op-btn {
  padding: 8px 28px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  background: #fafafa;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}
.op-btn.active {
  background: #1677ff;
  color: #fff;
}
.op-btn:hover:not(.active) {
  background: #f0f0f0;
}
.op-btn + .op-btn {
  border-left: 1px solid #e0e0e0;
}

.input-row {
  justify-content: center;
}

/* 按钮 */
.calc-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
}
.calc-btn {
  padding: 10px 32px;
  font-size: 15px;
  font-weight: 600;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;
}
.calc-btn:hover {
  background: #f5f5f5;
  border-color: #ccc;
}
.calc-btn.primary {
  background: #1677ff;
  color: #fff;
  border-color: #1677ff;
}
.calc-btn.primary:hover {
  background: #4096ff;
  border-color: #4096ff;
}

/* 结果 */
.result-box {
  margin-top: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%);
  border-radius: 10px;
  text-align: center;
  border: 1px solid #d9f0d9;
}
.result-label {
  font-size: 13px;
  color: #888;
  margin-bottom: 8px;
}
.result-value {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  word-break: break-all;
}
.result-detail {
  margin-top: 6px;
  font-size: 13px;
  color: #888;
}

.result-fade-enter-active, .result-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.result-fade-enter-from, .result-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* 时间差分段 */
.diff-section {
  margin-bottom: 8px;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #555;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

/* 帮助 */
.help-block {
  margin-top: 30px;
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.05);
}
.help-block h3 {
  font-size: 15px;
  color: #333;
  margin-bottom: 10px;
}
.help-block ul {
  list-style: disc;
  padding-left: 20px;
}
.help-block li {
  font-size: 13px;
  color: #777;
  line-height: 2;
}

@media (max-width: 480px) {
  .page-title { font-size: 22px; }
  .calc-panel { padding: 16px; }
  .calc-row { gap: 8px; }
  .calc-input { min-width: 100px; font-size: 14px; }
  .calc-input.short { min-width: 56px; width: 64px; }
  .op-btn { padding: 8px 20px; }
  .result-value { font-size: 18px; }
}
</style>
